import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { matchPath } from 'react-router-dom';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import BookmarkIcon from '@material-ui/icons/BookmarkBorder';
import ProfileIcon from '@material-ui/icons/AccountCircle';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { useDispatch, useSelector } from 'react-redux';
import Routes from 'Routes';
import {
  APP, DEFAULT_THEME_COLOR, FETCH_ASSETS_URL,
} from 'appenv';
import { UserSessionContext } from 'components/UserSession';
import { setOpen as setProfilePageOpen } from 'models/profileModal';
import BookmarksModal from 'components/modals/bookmarks/BookmarksModal';
import UserAvatar from 'components/UserAvatar';
import IconLogout from 'components/icons/Logout';
import { selectTotalBookmarkCount } from 'models/bookmarks';
import useMenuItems from 'components/appbar/useMenuItems';
import StyledMobileNavLink from 'components/appbar/mobile/StyledMobileNavLink';
import DrawerCustomPages from 'components/appbar/mobile/DrawerCustomPages';
import DrawerListItem from 'components/appbar/mobile/DrawerListItem';
import { selectLocale } from 'models/localization';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import useNameByLocale from 'hooks/useNameByLocale';
import useMyProfileTreatment from 'hooks/useMyProfileTreatment';
import useLogout from 'hooks/useLogout';

const IconContact = `${FETCH_ASSETS_URL}/assets/contact.svg`;

const StyledDrawer = withStyles(() => ({
  paper: {
    width: '240px',
  },
}))(Drawer);

const UserContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 53px 0 16px;
  flex-shrink: 0;
`;

const UserName = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 22px;
  color: #484848;
  margin-top: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledListContainer = styled.div`
  padding-top: ${({ hideUserSession }) => (hideUserSession ? 28 : 0)}px;
`;

const StyledNavButtonText = styled.div`
  color: #484848;
  text-decoration: none;
  margin-left: 8px;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
`;

const StyledLogoutBtn = styled.div`
  color: #484848;
  text-decoration: none;
  display: flex;
  align-items: center;
`;

const LogoutText = styled.span`
  margin-left: 12px;
  font-weight: bold;
  font-size: 16px;
  line-height: 22px;
`;

const StyledDrawerLink = styled(StyledMobileNavLink)`
  padding: 16px 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
`;

const LogoutButtonContainer = styled(StyledDrawerLink)`
  padding-bottom: 32px;
`;

const DrawerListContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const DrawerMiddleButtons = styled.div``;

const DrawerBottomButtons = styled.div``;

const MenuItemList = () => {
  const menuItems = useMenuItems();
  return <DrawerCustomPages customPages={menuItems} />;
};

const DrawerView = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const totalBookmarks = useSelector(selectTotalBookmarkCount);
  const {
    themeColor,
    features, chatroomConfig,
    pageNames: { profile },
  } = useSelector((state) => state.eventConfigs);
  const { userSession, canSee } = useContext(UserSessionContext);
  const { onLogoutClick } = useLogout();
  const [bookmarksModalOpen, setBookmarkOpen] = useState(false);
  const activeLinkStyle = useMemo(() => ({
    color: themeColor || DEFAULT_THEME_COLOR,
  }), [themeColor]);
  const locale = useSelector(selectLocale);

  const openBookmarks = useCallback(() => {
    onClose();
    setBookmarkOpen(true);
  }, [setBookmarkOpen, onClose]);

  const closeBookmarks = useCallback(() => {
    setBookmarkOpen(false);
  }, [setBookmarkOpen]);

  const hideUserSession = useMemo(() => (APP === 'ms624'), []);
  const handleClick = (key) => {
    window.trackingEventToFirebase(null, 'MainMenu_Click', 'CustomPage', key);
  };

  const userFullName = userSession?.fullName || '';

  useEffect(() => {
    const { $zopim } = window;
    if ($zopim) {
      $zopim(() => {
        $zopim.livechat.setName(userFullName);
      });
    }
  }, [userFullName]);

  const onHelpBtnClick = useCallback(() => {
    onClose();
    const { $zopim } = window;
    $zopim(() => {
      $zopim.livechat.setName(userFullName);
      $zopim.livechat.window.show();
    });
  }, [userFullName, onClose]);

  const nameByLocale = () => {
    switch (locale) {
      case 'ja':
        return `${userSession?.lastName} ${userSession?.firstName}`;
      case 'zh-Hans':
      case 'zh-Hant':
        return `${userSession?.lastName}${userSession?.firstName}`;
      default:
        return userSession?.name;
    }
  };

  const openProfilePage = () => {
    handleClick('Profile page');
    dispatch(setProfilePageOpen(true));
  };

  const profilePageTitle = findAvailableLocale(profile || {}, locale) || t('pages.profile', 'Profile');
  const enableMyProfile = useMyProfileTreatment();
  const name = useNameByLocale();

  return (
    <>
      {canSee('lobby') && <BookmarksModal open={bookmarksModalOpen} onClose={closeBookmarks} />}
      <StyledDrawer
        anchor="left"
        open={open}
        onClose={onClose}
      >
        {!hideUserSession && name && (
          <UserContainer>
            <UserAvatar name={name} />
            <UserName>
              {t('greeting', 'Hi, %{name}', { name })}
            </UserName>
          </UserContainer>
        )}
        <DrawerListContainer>
          <StyledListContainer hideUserSession={hideUserSession}>
            <List>
              <MenuItemList />
            </List>
          </StyledListContainer>
          <DrawerMiddleButtons>
            <List>
              {canSee('lobby') && (features.includes('bookmarks') && totalBookmarks !== 0) && (
                <DrawerListItem onClick={openBookmarks}>
                  <BookmarkIcon />
                  <StyledNavButtonText>
                    Bookmark
                  </StyledNavButtonText>
                </DrawerListItem>
              )}
              <DrawerListItem onClick={onHelpBtnClick}>
                <HelpOutlineIcon />
                <StyledNavButtonText>
                  {t('help', 'Help')}
                </StyledNavButtonText>
              </DrawerListItem>
            </List>
          </DrawerMiddleButtons>
          <DrawerBottomButtons>
            {
              canSee('contacts') && (
                <StyledDrawerLink
                  onClick={() => handleClick('Contacts page')}
                  to={Routes.contacts}
                  activeStyle={activeLinkStyle}
                  isActive={(match, location) => (
                    matchPath(location.pathname, {
                      path: [Routes.lobby, Routes.root],
                      exact: true,
                    })
                  )}
                >
                  <img src={IconContact} alt="my contacts" />
                  <StyledNavButtonText>
                    {t('contact.my_contact')}
                  </StyledNavButtonText>
                </StyledDrawerLink>
              )
            }
            {enableMyProfile && !hideUserSession && nameByLocale() && (
              <StyledDrawerLink
                href="#"
                as="a"
                onClick={openProfilePage}
                activeStyle={activeLinkStyle}
              >
                <ProfileIcon color="inherit" />
                <StyledNavButtonText>
                  {profilePageTitle}
                </StyledNavButtonText>
              </StyledDrawerLink>
            )}
            {
              !hideUserSession && nameByLocale() && (
                <LogoutButtonContainer href="#" as="a">
                  <StyledLogoutBtn onClick={onLogoutClick}>
                    <IconLogout color="#484848" />
                    <LogoutText>
                      {t('logout', 'Logout')}
                    </LogoutText>
                  </StyledLogoutBtn>
                </LogoutButtonContainer>
              )
            }
          </DrawerBottomButtons>
        </DrawerListContainer>
      </StyledDrawer>
    </>
  );
};

DrawerView.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

DrawerView.defaultProps = {
  open: false,
  onClose: () => { },
};

export default DrawerView;
